<section class="header">
  <div class="title">Import Information</div>
  <button class="close-button" mat-icon-button (click)="dismiss()" [tabIndex]="-1">
    <mat-icon [svgIcon]="'close'"></mat-icon>
  </button>
</section>

<section class="body">
  <mat-horizontal-stepper labelPosition="bottom" #importStepper>
    <!-- Step 1 -->
    <mat-step [stepControl]="stepOneForm" label="Initial Info">
      <ng-template matStepLabel>Initial Info</ng-template>
      <ng-template matStepContent>
        <ng-container *ngIf="!importFieldsError$; else NoFields">
          <form [formGroup]="stepOneForm" class="step-container">
            <section class="step-wrapper">
              <section class="mb-4">
                <app-alert-banner [type]="'info'">
                  <p><strong>Multiselect Delimiter:</strong> please use | as a delimiter for multiselect fields.</p>
                </app-alert-banner>
              </section>
              <!-- Object Type -->
              <mat-form-field class="w-full">
                <mat-label>Object type:</mat-label>
                <input
                  *ngIf="isEditMode"
                  class="disabled-input"
                  matInput
                  [formControlName]="'object_type'"
                  [readonly]="true"
                />
                <mat-select
                  *ngIf="!isEditMode"
                  [formControlName]="'object_type'"
                  [disableOptionCentering]="true"
                  (selectionChange)="onObjectTypeChange($event)"
                  required
                >
                  <input
                    class="ts-select-search-input"
                    [formControl]="importEnableObjectsCtrl"
                    [placeholder]="'Search...'"
                  />
                  <mat-option *ngIf="!filteredImportEnableObjects?.length" disabled>No matches found</mat-option>
                  <mat-option *ngFor="let object of filteredImportEnableObjects" [value]="object.name">
                    {{ object.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <!-- Object Type Errors -->
              <ng-container *ngIf="stepOneForm.get('object_type').errors?.required">
                <ng-template [ngIf]="stepOneForm.get('object_type').touched">
                  <mat-error>Please select an object option.</mat-error>
                </ng-template>
              </ng-container>

              <mat-divider class="my-3"></mat-divider>

              <!-- Import Mode -->
              <mat-form-field class="w-full" floatLabel="always">
                <mat-label>Select mode:</mat-label>
                <mat-select
                  [formControlName]="'mode'"
                  [disableOptionCentering]="true"
                  [placeholder]="importModeFieldPlaceholder"
                  (selectionChange)="onModeChange($event)"
                  required
                >
                  <mat-option *ngFor="let mode of modeOptions" [value]="mode">
                    {{ mode | formatSnakeCase }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <!-- Import Mode Errors -->
              <ng-container *ngIf="stepOneForm.get('mode').errors?.required">
                <ng-template [ngIf]="stepOneForm.get('mode').touched">
                  <mat-error>Please select a mode option.</mat-error>
                </ng-template>
              </ng-container>

              <mat-divider class="my-3"></mat-divider>

              <!-- File Import -->
              <section class="w-full">
                <input
                  #fileUpload
                  type="file"
                  hidden
                  (change)="onImportFileChange($event)"
                  [accept]="acceptedFileExtensions"
                  required
                />
                <div class="flex items-center">
                  <ts-button color="primary" (click)="fileUpload.click()">
                    <mat-icon class="mr-1">attach_file</mat-icon>
                    Upload file
                  </ts-button>

                  <span class="ml-2">{{
                    stepOneForm.get('uploaded_file').value?.name ||
                      this.dialogData.import?.document.public_name ||
                      'No file uploaded yet.'
                  }}</span>
                </div>

                <p class="text-sm mt-2">
                  Accepted formats are <strong>{{ acceptedFileExtensions.join(', ') }}</strong
                  >.
                </p>
              </section>
              <!-- File Import Errors -->
              <ng-container
                *ngIf="stepOneForm.get('uploaded_file').errors?.required && !stepOneForm.get('uploaded_file').pristine"
              >
                <ng-template [ngIf]="!stepOneForm.get('uploaded_file').value">
                  <mat-error>Please select a file to upload.</mat-error>
                </ng-template>
              </ng-container>

              <!-- Step 1 Errors -->
              <section *ngIf="firstStepValidationErrors?.length" class="mt-2">
                <app-alert-banner [type]="'error'">
                  <p *ngFor="let error of firstStepValidationErrors">{{ error }}.</p>
                  <p class="mt-1">You can fix the current file, upload a new one or choose a different mode option.</p>
                </app-alert-banner>
              </section>
            </section>
            <section class="button-actions" [ngClass]="{ 'justify-center': stepperIndex === 0 }">
              <button
                class="proceed-button"
                mat-raised-button
                [color]="'primary'"
                [disabled]="checkStepperFormErrors(stepOneForm)"
                DebounceClick
                (debounceClick)="onFirstStepNextClick(stepOneForm)"
              >
                <ng-container *ngIf="!isStepLoading; else loadingState">
                  Next
                  <mat-icon [svgIcon]="'chevron_right'"></mat-icon>
                </ng-container>
              </button>
            </section>
          </form>
        </ng-container>
      </ng-template>
    </mat-step>

    <!-- Step 2 -->
    <mat-step [stepControl]="stepTwoForm" label="Fields Mapping">
      <ng-template matStepLabel>Fields Mapping</ng-template>
      <ng-template matStepContent>
        <ng-container *ngIf="!updateImportError$; else UnableToUpdateImport">
          <form [formGroup]="stepTwoForm" class="step-container">
            <section class="step-wrapper">
              <section class="mt-4">
                <app-alert-banner [type]="'info'">
                  <p>
                    <strong>Fields Mapping</strong> is critical to ensure that data is imported accurately.
                    <span *ngIf="this.isEditMode">
                      Review the <strong>TermSheet Fields</strong> column to verify that the auto-populated suggestions
                      are correct.
                    </span>
                  </p>
                </app-alert-banner>
              </section>
              <section class="fields-mapping my-4">
                <ng-container formArrayName="mapping">
                  <section class="fields-mapping__header">
                    <p [ngClass]="dataPreview ? 'w-2/6' : 'w-3/6'"><strong>Import Headers</strong></p>
                    <ng-container *ngIf="dataPreview">
                      <p class="w-2/6 pl-4"><strong>Data Preview</strong></p>
                    </ng-container>
                    <p class="flex items-center" [ngClass]="dataPreview ? 'w-2/6' : 'w-3/6'">
                      <strong>Termsheet Field</strong>
                      <mat-icon
                        class="ml-1"
                        [color]="'primary'"
                        [svgIcon]="'info'"
                        [matTooltip]="'Only non-hidden and marked as Importable fields are available for mapping.'"
                        [matTooltipPosition]="'above'"
                      ></mat-icon>
                    </p>
                  </section>
                  <ng-container *ngFor="let mappingItem of mapping.controls; let i = index; last as isLast">
                    <section class="fields-mapping__container" [formGroupName]="i">
                      <p [ngClass]="dataPreview ? 'w-2/6' : 'w-3/6'">
                        <strong>{{ mappingItem.controls['header'].value }}</strong>
                      </p>
                      <ng-container *ngIf="dataPreview">
                        <div class="w-2/6 pl-4">
                          <ul *ngFor="let dataArray of dataPreview[mappingItem.controls['header'].value]">
                            <li class="text-left">{{ dataArray }}</li>
                          </ul>
                        </div>
                      </ng-container>
                      <section class="flex items-center" [ngClass]="dataPreview ? 'w-2/6' : 'w-3/6'">
                        <button
                          mat-icon-button
                          [color]="'primary'"
                          class="mr-2"
                          [matTooltip]="'Clear selection'"
                          (click)="clearFieldSelection(i)"
                        >
                          <mat-icon [svgIcon]="'delete'"></mat-icon>
                        </button>
                        <mat-form-field class="w-full">
                          <mat-select
                            #selectElement
                            formControlName="field"
                            [multiple]="false"
                            [disableOptionCentering]="true"
                          >
                            <input
                              class="ts-select-search-input"
                              [formControl]="mappingItem?.controls['filterValue']"
                              [placeholder]="'Search...'"
                            />
                            <mat-option
                              *ngIf="
                                !(
                                  filteredFieldsList
                                  | filterBy: ['label']:(mappingItem.controls['filterValue'].valueChanges | async)
                                ).length
                              "
                            >
                              No matches found
                            </mat-option>
                            <mat-option
                              *ngFor="
                                let field of filteredFieldsList
                                  | filterBy: ['label']:(mappingItem.controls['filterValue'].valueChanges | async)
                              "
                              [value]="field.name"
                            >
                              {{ field.label }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>
                        <button
                          mat-icon-button
                          [color]="'primary'"
                          class="ml-2"
                          [matTooltip]="'Create Custom Field'"
                          DebounceClick
                          (debounceClick)="openCustomFieldDialog(i)"
                        >
                          <mat-icon [svgIcon]="'add'"></mat-icon>
                        </button>
                      </section>
                    </section>
                    <mat-divider *ngIf="!isLast"></mat-divider>
                  </ng-container>
                </ng-container>
              </section>
              <ng-container *ngIf="!isFieldsMappingEmpty()">
                <section class="mb-4">
                  <app-alert-banner>
                    <p>In order to proceed, you must assign a field to each header.</p>
                  </app-alert-banner>
                </section>
              </ng-container>
              <ng-container *ngIf="!isFieldsMappingDuplicated()">
                <section class="mb-4">
                  <app-alert-banner [type]="'error'">
                    <p>
                      Duplicated fields mapping is not allowed. Please, select a different
                      <strong>Termsheet Field</strong> for each header and click <strong>Next</strong> again.
                    </p>
                  </app-alert-banner>
                </section>
              </ng-container>
              <ng-container *ngIf="idMappingErrors?.length">
                <ng-container *ngFor="let error of idMappingErrors">
                  <app-alert-banner class="mb-2" [type]="'error'">
                    {{ error }}
                  </app-alert-banner>
                </ng-container>
              </ng-container>
            </section>
            <section class="button-actions">
              <button class="back-button" mat-stroked-button (click)="onSecondStepBackClick()">
                <mat-icon [svgIcon]="'chevron_left'"></mat-icon>
                Back
              </button>
              <button
                class="proceed-button"
                mat-raised-button
                [color]="'primary'"
                [disabled]="!isFieldsMappingEmpty() || !isFieldsMappingDuplicated()"
                DebounceClick
                (debounceClick)="onSecondStepNextClick()"
              >
                <ng-container *ngIf="!isStepLoading; else loadingState">
                  Next
                  <mat-icon [svgIcon]="'chevron_right'"></mat-icon>
                </ng-container>
              </button>
            </section>
          </form>
        </ng-container>
      </ng-template>
    </mat-step>

    <!-- Step 3 -->
    <mat-step label="Review your choices">
      <ng-template matStepLabel>Review your choices</ng-template>
      <ng-template matStepContent>
        <section class="step-container">
          <section class="step-wrapper">
            <p>
              Object Type: <strong>{{ fetchFormValues(stepOneForm, 'object_type') }}</strong>
            </p>
            <mat-divider class="my-2"></mat-divider>
            <p>
              Imported File:
              <strong>{{
                fetchFormValues(stepOneForm, 'uploaded_file').name || dialogData.import.document.public_name
              }}</strong>
            </p>
            <mat-divider class="my-2"></mat-divider>
            <p>
              Import Mode:
              <strong>{{
                fetchFormValues(stepOneForm, 'mode').value || dialogData.import.mode | formatSnakeCase
              }}</strong>
            </p>
            <section class="review__container mt-3 mb-6 shadow-sm">
              <section class="review__container--title">
                <p class="text-lg"><strong>Import Headers</strong></p>
                <p class="text-lg"><strong>Termsheet Fields</strong></p>
              </section>
              <mat-divider class="mb-2"></mat-divider>
              <ng-container *ngFor="let mappedField of mapping.controls; last as isLast">
                <section class="review__container--list" [ngClass]="{ 'mb-2': isLast }">
                  <p>
                    <strong>{{ mappedField.value.header }}</strong>
                  </p>
                  <p>
                    <strong>{{
                      mappedField.value.field?.label
                        ? mappedField.value.field.label
                        : displayMappedFieldLabel(mappedField.value.field)
                    }}</strong>
                  </p>
                </section>
                <mat-divider *ngIf="!isLast" class="my-2"></mat-divider>
              </ng-container>
            </section>
            <section class="mb-4">
              <app-alert-banner [type]="'info'">
                <p>
                  Click <strong>Done</strong> to prepare the file. To complete the import, expand the newly created
                  import record with the status <strong>Ready to Run</strong> then click on <strong>Run</strong> button.
                </p>
              </app-alert-banner>
            </section>
          </section>
          <section class="button-actions">
            <button class="back-button" mat-stroked-button matStepperPrevious>
              <mat-icon [svgIcon]="'chevron_left'"></mat-icon>
              Back
            </button>
            <button
              class="proceed-button"
              mat-raised-button
              [color]="'primary'"
              type="submit"
              DebounceClick
              (debounceClick)="saveAndClose()"
            >
              Done
            </button>
          </section>
        </section>
      </ng-template>
    </mat-step>
  </mat-horizontal-stepper>
</section>

<ng-template #loadingState>
  <mat-spinner diameter="24" [strokeWidth]="2" [mode]="'indeterminate'" [color]="'primary'"></mat-spinner>
</ng-template>

<ng-template #NoFields>
  <ng-container *ngIf="importFieldsError$ | async as error; else Loading">
    <section class="my-6 w-full h-full flex flex-col justify-center items-center">
      <h5 class="text-center">Oops! Something went wrong...</h5>
      <h6 class="text-center">The service responsible for fetching related fields seems to be offline.</h6>
      <h6 class="text-center">You can try again in a while.</h6>
    </section>
    <section class="button-actions">
      <button mat-raised-button [color]="'primary'" (click)="dismiss()">Close</button>
    </section>
  </ng-container>
  <ng-template #Loading>
    <section class="my-12 w-full h-full flex justify-center items-center">
      <mat-spinner diameter="64" [strokeWidth]="4" [mode]="'indeterminate'" [color]="'primary'"></mat-spinner>
    </section>
  </ng-template>
</ng-template>

<ng-template #UnableToUpdateImport>
  <ng-container *ngIf="updateImportError$ | async as error; else Loading">
    <section class="fields-mapping my-6">
      <h5 class="text-center">Oops! Something went wrong...</h5>
      <h6 class="text-center">Unable to map columns from uploaded file.</h6>
      <h6 class="text-center">You can try again in a while.</h6>
    </section>
    <section class="button-actions">
      <button mat-raised-button [color]="'primary'" (click)="dismiss()">Close</button>
    </section>
  </ng-container>
  <ng-template #Loading>
    <section class="fields-mapping my-6 flex justify-center items-center">
      <mat-spinner diameter="64" [strokeWidth]="4" [mode]="'indeterminate'" [color]="'primary'"></mat-spinner>
    </section>
  </ng-template>
</ng-template>
